import type { CommunitiesStatsTimeGraphType } from "@packages/sdk";
import { CommunityType } from "@packages/sdk";
import type { DashboardMetricConfig } from "@packages/ui";

/**
 * The metrics shown as clickable tiles below the dashboard chart, in display
 * order. Selecting a tile swaps the chart to that metric's `time-graph`.
 */
export const DASHBOARD_METRICS: DashboardMetricConfig[] = [
  {
    type: "completed-prayers",
    labelKey: "parish_admin_dashboard_prayers_completed",
    descriptionKey: "dashboard_metric_prayers_completed_description",
    analytics: "prayers_completed",
    totalsField: "prayers_completed",
  },
  {
    type: "minutes-prayed",
    labelKey: "total_minutes_prayed",
    descriptionKey: "dashboard_metric_minutes_prayed_description",
    analytics: "minutes_prayed",
    totalsField: "total_minutes_prayed",
  },
  {
    type: "intentions",
    labelKey: "total_intentions",
    descriptionKey: "dashboard_metric_intentions_description",
    analytics: "intentions",
    totalsField: "intentions_count",
    excludeForSchool: true,
  },
  {
    type: "members",
    labelKey: "total_members",
    descriptionKey: "dashboard_metric_members_description",
    analytics: "members",
    totalsField: "member_count",
    excludeForSchool: true,
  },
  {
    type: "active-members",
    labelKey: "dashboard_metric_active_members_label",
    descriptionKey: "dashboard_metric_active_members_description",
    analytics: "active_members",
    totalsField: "active_members",
    isPercent: true,
    excludeForSchool: true,
    excludeForCommunityTypes: [CommunityType.parish, CommunityType.small_group],
  },
];

export const DEFAULT_DASHBOARD_METRIC: CommunitiesStatsTimeGraphType =
  "completed-prayers";

/**
 * Temporary allowlist for COM-348: re-enables the "active-members" tile for
 * this one school while it stays hidden for every other school.
 */
const ACTIVE_MEMBERS_SCHOOL_ALLOWLIST = [24505];

/**
 * The metrics a community can actually see. Drive both the tiles and the chart
 * from this so a hidden metric can never be the selected one — e.g. a school
 * admin opening a `?dashboardMetric=members` bookmark.
 */
export const getVisibleDashboardMetrics = ({
  communityType,
  isSchool,
  communityId,
}: {
  communityType: CommunityType | undefined;
  isSchool: boolean;
  communityId: number;
}): DashboardMetricConfig[] =>
  DASHBOARD_METRICS.filter((metric) => {
    if (
      communityType &&
      metric.excludeForCommunityTypes?.includes(communityType)
    ) {
      return false;
    }
    if (!isSchool || !metric.excludeForSchool) return true;
    if (metric.type === "active-members") {
      return ACTIVE_MEMBERS_SCHOOL_ALLOWLIST.includes(communityId);
    }
    return false;
  });

/**
 * Resolves the selected metric against `metrics`, falling back to the first
 * visible one when `type` is unknown or hidden for this community.
 */
export const getDashboardMetric = (
  type: CommunitiesStatsTimeGraphType,
  metrics: DashboardMetricConfig[] = DASHBOARD_METRICS,
): DashboardMetricConfig =>
  metrics.find((metric) => metric.type === type) ?? metrics[0];
